import { useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface FeedbackDialogProps {
  collapsed?: boolean;
}

const FeedbackDialog = ({ collapsed = false }: FeedbackDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const feedbackTypes = [
    { value: "bug", label: t("feedback.typeBug") },
    { value: "feature", label: t("feedback.typeFeature") },
    { value: "improvement", label: t("feedback.typeImprovement") },
    { value: "general", label: t("feedback.typeGeneral") },
  ];

  const handleSubmit = async () => {
    if (!user || !message.trim()) return;

    setSubmitting(true);
    try {
      const feedbackLabel = feedbackTypes.find(ft => ft.value === type)?.label || type;
      const { error: ticketError } = await supabase.from("support_tickets").insert({
        name: user.email || t("feedback.portalUser"),
        email: user.email || "",
        subject: `[Feedback] ${feedbackLabel}`,
        message: message.trim(),
        ticket_number: "",
      });
      if (ticketError) throw ticketError;

      await supabase.from("feedback").insert({
        user_id: user.id,
        type,
        message: message.trim(),
        page_url: location.pathname,
      });

      toast({
        title: t("feedback.successTitle"),
        description: t("feedback.successDescription"),
      });

      setMessage("");
      setType("general");
      setOpen(false);
    } catch (error: any) {
      toast({
        title: t("feedback.errorTitle"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:bg-secondary hover:text-foreground w-full",
            collapsed && "justify-center"
          )}
        >
          <MessageSquarePlus className="w-5 h-5 shrink-0" />
          {!collapsed && <span>{t("feedback.trigger")}</span>}
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("feedback.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("feedback.dialogDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t("feedback.typeLabel")}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {feedbackTypes.map((ft) => (
                  <SelectItem key={ft.value} value={ft.value}>
                    {ft.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("feedback.messageLabel")}</Label>
            <Textarea
              placeholder={t("feedback.messagePlaceholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("feedback.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !message.trim()}>
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <MessageSquarePlus className="w-4 h-4 mr-2" />
            )}
            {t("feedback.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
